import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useMemo, useState } from "react";

import { geometryToWkt, parseBoundaryGeometry } from "@/helper/map-utils";

import { Button } from "@/components/ui/button";
import { Check, Copy } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { CityInput } from "types";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useFormContext } from "react-hook-form";

const WAZE_FEED_URL_PATTERN =
  /^https:\/\/www\.waze\.com\/row-partnerhub-api\/partners\/\d+\/waze-feeds\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(\?format=\d+)?$/;

export const WazeConfig = () => {
  const { control, watch, setValue } = useFormContext<CityInput>();
  const wazeFeedUrl = watch("options.wazeFeedUrl");
  const [manuallyEnabled, setManuallyEnabled] = useState(false);
  const [copied, setCopied] = useState(false);
  const showWazeFeed = !!wazeFeedUrl || manuallyEnabled;

  const handleWazeFeedToggle = (checked: boolean | "indeterminate") => {
    const isEnabled = checked === true;
    setManuallyEnabled(isEnabled);
    if (!isEnabled) setValue("options.wazeFeedUrl", undefined, { shouldValidate: true });
  };

  const boundaries = watch("boundaries");
  const wkt = useMemo(() => {
    const geometry = parseBoundaryGeometry(boundaries);
    if (!geometry) return "";
    try {
      return geometryToWkt(geometry);
    } catch {
      return "";
    }
  }, [boundaries]);

  const handleCopyWkt = async () => {
    if (!wkt || copied) return;
    try {
      await navigator.clipboard.writeText(wkt);
      setCopied(true);
      toast.success("Périmètre WKT copié dans le presse-papiers", { style: { color: "#228B22" } });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Échec de la copie du périmètre");
    }
  };

  return (
    <div className="px-4 pt-4 pb-2 text-sm space-y-6">
      <div>
        <div className="flex items-center space-x-2">
          <Checkbox id="wazeFeed" checked={showWazeFeed} onCheckedChange={handleWazeFeedToggle} />
          <label htmlFor="wazeFeed" className="text-sm font-medium leading-none">
            Activer le flux Waze
          </label>
        </div>
        {showWazeFeed && (
          <div className="mt-3">
            <FormField
              control={control}
              name="options.wazeFeedUrl"
              rules={{
                pattern: {
                  value: WAZE_FEED_URL_PATTERN,
                  message: "URL invalide. Le format doit correspondre à une URL de feed Waze valide.",
                },
              }}
              render={({ field: { value, ...rest } }) => (
                <FormItem>
                  <FormLabel>URL du flux Waze</FormLabel>
                  <FormControl>
                    <Input
                      value={value ?? ""}
                      {...rest}
                      placeholder="https://www.waze.com/row-partnerhub-api/partners/12345/waze-feeds/a1b2c3d4-e5f6-7890-abcd-ef1234567890?format=1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <h1 className="font-bold text-sm">Périmètre (WKT)</h1>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopyWkt} disabled={!wkt}>
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <textarea
          className="w-full h-64 bg-primary border-2 text-white text-sm text-bold px-1 resize-none"
          disabled
          value={wkt}
        />
      </div>
    </div>
  );
};
